/**
 * CaseloadScreen — list of students owned by current user (PRD §3 #1,
 * issue #436 for the new toolbar + bulk-archive affordances).
 *
 * For v1.0-A: shows a list of students with a disorder_category
 * chip + last session date + active goals count. Add-student CTA
 * at the top. Tapping a row opens the student detail.
 *
 * Free-tier cap is enforced here: if `students count >=
 * users.students_enabled` we replace the Add-student CTA with
 * "Upgrade to Pro". The `useProAccess` hook reads from Supabase.
 *
 * Desktop additions (issues #433 + #436):
 *   - Multi-column grid at ≥1024px (auto-fill 320px columns).
 *   - Search / filter / sort toolbar (CaseloadToolbar) with
 *     match count, "Needs new session" toggle, active-goals
 *     filter, group-by-last-session sectioning, and bulk-select.
 *   - Empty state copy moves to the toolbar slot, not as a card.
 *   - Mobile layout (<1024px) keeps the vertical list but adds
 *     the toolbar with a slide-up filter sheet.
 *   - Filter state persists across navigation via sessionStorage.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuth } from '../lib/auth/useAuth';
import {
  readAccessState,
  type AccessState,
} from '../lib/paywall/access';
import {
  type StudentSummary,
  type StudentListRow,
  summarizeStudent,
} from '../lib/effectiveslp/students';
import { labelForDisorderCategory, formatShortDate } from '../lib/effectiveslp/domain';
import { StudentAvatar } from '../lib/effectiveslp/StudentAvatar';
import {
  EmptyState,
  StudentsIllustration,
} from '../components/EmptyState';
import { CaseloadSkeleton } from '../components/Skeleton';
import { BulkActionBar } from '../components/BulkActionBar';
import { humanError } from '../lib/ui/errorCopy';
import { useBreakpoint } from '../lib/ui/useBreakpoint';
import {
  CaseloadToolbar,
  type ActiveGoalsFilter,
} from '../components/CaseloadToolbar';
import {
  filterCaseload,
  groupByLastSession,
  readQueryFromStorage,
  sanitizeQuery,
  sortCaseload,
  writeQueryToStorage,
  type CaseloadQuery,
  type SortKey,
} from '../lib/caseload/filters';

interface Props {
  onAddStudent: () => void;
  onOpenStudent: (id: string) => void;
  onOpenPaywall: () => void;
  onOpenSettings: () => void;
  onSignOut: () => void;
}

const CATEGORY_ALL = 'all';

const DEFAULT_QUERY: CaseloadQuery = {
  search: '',
  goalArea: CATEGORY_ALL,
  needsNewSession: false,
  activeGoals: 'all',
  sort: 'last_session',
};

export function CaseloadScreen({
  onAddStudent,
  onOpenStudent,
  onOpenPaywall,
  onOpenSettings,
  onSignOut,
}: Props): ReactElement {
  const { user, signOut } = useAuth();
  const [students, setStudents] = useState<StudentSummary[]>([]);
  const [access, setAccess] = useState<AccessState>({ kind: 'unknown' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Toolbar state — desktop + mobile share the same CaseloadQuery.
  // #436 acceptance criterion #7: persist across navigation via
  // sessionStorage so coming back to /caseload restores the
  // search / filter / sort the clinician left on.
  const [search, setSearch] = useState(DEFAULT_QUERY.search);
  const [goalArea, setGoalArea] = useState<string>(DEFAULT_QUERY.goalArea);
  const [needsNewSession, setNeedsNewSession] = useState(
    DEFAULT_QUERY.needsNewSession,
  );
  const [activeGoals, setActiveGoals] = useState<ActiveGoalsFilter>(
    DEFAULT_QUERY.activeGoals,
  );
  const [sort, setSort] = useState<SortKey>(DEFAULT_QUERY.sort);

  // Bulk-select state.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [archiveNotice, setArchiveNotice] = useState<string | null>(null);
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  const bp = useBreakpoint();
  const isDesktop = bp === 'desktop' || bp === 'wide';

  // Hydrate toolbar state from sessionStorage on first mount.
  // We do this in an effect, not in `useState`'s lazy initializer,
  // because sessionStorage isn't available during SSR — and the
  // toolbar's first paint is unaffected because the state falls
  // back to defaults if hydration returns null.
  useEffect(() => {
    const stored = readQueryFromStorage();
    const sanitized = sanitizeQuery(stored);
    if (!sanitized) return;
    setSearch(sanitized.search);
    setGoalArea(sanitized.goalArea);
    setNeedsNewSession(sanitized.needsNewSession);
    setActiveGoals(sanitized.activeGoals);
    setSort(sanitized.sort);
  }, []);

  // Persist any query change. Wrapping setSearch etc. here would
  // be more direct, but a single effect keyed off the five values
  // makes the serialised shape self-documenting.
  useEffect(() => {
    writeQueryToStorage({
      search,
      goalArea,
      needsNewSession,
      activeGoals,
      sort,
    });
  }, [search, goalArea, needsNewSession, activeGoals, sort]);

  // Ref read by the `load` effect cleanup so an unmount mid-fetch
  // can short-circuit setState (#520). Captured by reference, so
  // `load` sees the latest value even after the fetch's `await`
  // resolves (#452 dedupe originally passed a snapshot, which
  // silently neutered this guard).
  const cancelledRef = useRef(false);

  // Single source of truth for the caseload fetch — used by the
  // initial-load effect AND the error-retry CTA so the two paths
  // can't drift (#452 tech debt). `isCancelled()` is a getter so
  // `load` can observe cleanup that fires after the fetch starts;
  // the retry click never invokes a cleanup, so it observes false.
  const load = useCallback(async (isCancelled: () => boolean = () => false) => {
    setLoading(true);
    try {
      const { data, error: qErr } = await supabase
        .rpc('list_students_with_counts');
      if (isCancelled()) return;
      if (qErr) {
        setError(humanError(qErr));
        return;
      }
      setStudents(
        ((data ?? []) as StudentListRow[])
          .filter((r) => !r.archived)
          .map(summarizeStudent),
      );
    } catch (err) {
      if (!isCancelled()) {
        setError(humanError(err));
      }
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    void load(() => cancelledRef.current);
    return () => {
      cancelledRef.current = true;
    };
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const next = await readAccessState();
        if (!cancelled) setAccess(next);
      } catch {
        // Surface 'unknown' if the read fails.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [students.length]);

  const isPro = access.kind === 'pro';
  const freeExhausted = access.kind === 'free-exhausted';

  // Filter + sort the caseload. The toolbars run their debounce +
  // selection logic; the screen aggregates into a derived list.
  const visible = useMemo(() => {
    const filtered = filterCaseload(students, {
      search,
      goalArea,
      needsNewSession,
      activeGoals,
      sort,
    });
    return sortCaseload(filtered, sort);
  }, [students, search, goalArea, needsNewSession, activeGoals, sort]);

  // Group by last-session bucket when the user has selected
  // "Last session" as the sort axis. Anything else renders as a
  // single section-less grid.
  const groupedSections = useMemo(
    () => (sort === 'last_session' ? groupByLastSession(visible) : null),
    [visible, sort],
  );

  // Toolbar helpers.
  // Sort is a view choice, not a filter — it does NOT influence
  // whether "Clear filters" surfaces. (#487: the toolbar's Clear
  // filters button deliberately leaves sort untouched, and the
  // filtered-empty-state's Clear filters CTA must agree.)
  const hasActiveFilters =
    search.length > 0 ||
    goalArea !== CATEGORY_ALL ||
    needsNewSession ||
    activeGoals !== 'all';

  const clearAllFilters = useCallback(() => {
    setSearch('');
    setGoalArea(CATEGORY_ALL);
    setNeedsNewSession(false);
    setActiveGoals('all');
    // sort is intentionally preserved — it's a view preference,
    // not a filter. (#487)
  }, []);

  // Bulk-select handlers.
  const toggleSelectMode = useCallback(() => {
    setSelectMode((m) => {
      if (m) setSelectedIds(new Set());
      return !m;
    });
    // Clear any partial-success notice from a prior archive run
    // so the user doesn't see stale messaging when they re-enter
    // select mode (#488).
    setArchiveNotice(null);
  }, []);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Auto-enter select mode the first time the user clicks a
  // checkbox-corner toggle. We don't gate the checkbox itself on
  // `selectMode` because the spec calls for hover-only visibility
  // on desktop and a "Select" toolbar button on mobile — but the
  // act of clicking counts as "wants to select".
  const handleCardClick = useCallback(
    (id: string) => {
      if (selectMode || selectedIds.size > 0) {
        toggleSelected(id);
        return;
      }
      onOpenStudent(id);
    },
    [selectMode, selectedIds.size, toggleSelected, onOpenStudent],
  );

  const onArchive = useCallback(() => {
    if (selectedIds.size === 0) return;
    setConfirmingArchive(true);
  }, [selectedIds.size]);

  const cancelArchive = useCallback(() => {
    setConfirmingArchive(false);
    setArchiveError(null);
  }, []);

  const confirmArchive = useCallback(async () => {
    if (selectedIds.size === 0) {
      setConfirmingArchive(false);
      return;
    }
    const selectedCount = selectedIds.size;
    setArchiving(true);
    setArchiveError(null);
    setArchiveNotice(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc(
        'bulk_archive_students',
        { p_ids: Array.from(selectedIds) },
      );
      if (rpcErr) {
        setArchiveError(humanError(rpcErr));
        return;
      }
      // The RPC returns the mutated rowcount so the client can
      // detect partial success (#488): rows outside the caller's
      // ownership are silently skipped. We must NOT assume every
      // selected id was archived — an un-archived student vanishing
      // from the local list until the next refetch is a confusing
      // UX for the clinician.
      const updatedRowCount = typeof data === 'number' ? data : null;
      setSelectedIds(new Set());
      setSelectMode(false);
      setConfirmingArchive(false);
      if (updatedRowCount === null) {
        // The RPC didn't return a rowcount (older server), so we
        // can't confirm partial success. Optimistically remove the
        // selected ids and surface a generic success.
        setStudents((prev) =>
          prev.filter((s) => !selectedIds.has(s.id)),
        );
        return;
      }
      if (updatedRowCount === 0) {
        // Nothing was archived — leave the local list alone and
        // ask the clinician to refresh so they can reconcile.
        setArchiveError(
          'No students were archived. Please refresh and try again.',
        );
        return;
      }
      if (updatedRowCount < selectedCount) {
        // Partial success: refetch the caseload so the un-archived
        // rows reappear, and surface a notice so the clinician
        // understands the mismatch.
        setArchiveNotice(
          `Archived ${updatedRowCount} of ${selectedCount} students. ` +
            'Some could not be archived (they may have been moved or ' +
            'deleted). Refreshed the caseload to show the latest.',
        );
        void load();
        return;
      }
      if (updatedRowCount > selectedCount) {
        // Anomaly: the server reports archiving more rows than we
        // selected. This shouldn't happen — possible causes are an
        // RPC bug, a trigger archiving dependent rows, or a future
        // RPC change. Optimistically filtering would silently hide
        // rows the user never asked to archive (#535). Log loudly
        // and refetch so the local list reflects the true state.
        console.warn(
          `[effectiveslp] bulk_archive_students returned rowcount ` +
            `${updatedRowCount} for ${selectedCount} selected ids. ` +
            'Refetching caseload instead of applying optimistic filter.',
        );
        setArchiveNotice(
          `Archived ${selectedCount} students. Refreshed the caseload ` +
            'to confirm the latest state.',
        );
        void load();
        return;
      }
      // Full success: archived every selected id.
      setStudents((prev) => prev.filter((s) => !selectedIds.has(s.id)));
    } catch (err) {
      setArchiveError(humanError(err));
    } finally {
      setArchiving(false);
    }
  }, [selectedIds, load]);

  const cancelSelect = useCallback(() => {
    setSelectedIds(new Set());
    setSelectMode(false);
  }, []);

  const bulkActionBar =
    selectedIds.size > 0 ? (
      <BulkActionBar
        selectedCount={selectedIds.size}
        onArchive={onArchive}
        onCancel={cancelSelect}
        archiving={archiving}
      />
    ) : null;

  return (
    <main className="screen caseload">
      <header className="screen-header">
        <h1>Caseload</h1>
        <p className="tagline">
          Signed in as {user?.email ?? '(unknown)'}.{' '}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              void (async () => {
                await signOut();
                onSignOut();
              })();
            }}
          >
            Sign out
          </button>
        </p>
      </header>

      {freeExhausted && !isPro ? (
        <section className="auth-confirm" aria-live="polite">
          <p>
            You&rsquo;ve used the Free-tier student cap. Upgrade to Pro
            for unlimited students.
          </p>
          <button
            type="button"
            className="snap-button"
            onClick={onOpenPaywall}
          >
            Upgrade to Pro
          </button>
        </section>
      ) : (
        <button
          type="button"
          className="snap-button"
          onClick={onAddStudent}
        >
          + Add student
        </button>
      )}

      <button
        type="button"
        className="link-button"
        onClick={onOpenSettings}
      >
        Settings
      </button>

      <CaseloadToolbar
        searchValue={search}
        onSearchChange={setSearch}
        goalArea={goalArea}
        onGoalAreaChange={setGoalArea}
        needsNewSession={needsNewSession}
        onNeedsNewSessionChange={setNeedsNewSession}
        activeGoals={activeGoals}
        onActiveGoalsChange={setActiveGoals}
        sort={sort}
        onSortChange={setSort}
        totalStudents={students.length}
        filteredCount={visible.length}
        hasActiveFilters={hasActiveFilters}
        selectMode={selectMode}
        onToggleSelectMode={toggleSelectMode}
        bulkActionBar={bulkActionBar}
      />

      {archiveError && (
        <p
          className="caseload-empty-filter tagline"
          role="alert"
        >
          {archiveError}
        </p>
      )}

      {archiveNotice && !archiveError && (
        <p
          className="caseload-empty-filter tagline"
          role="status"
          aria-live="polite"
        >
          {archiveNotice}
        </p>
      )}

      {loading && <CaseloadSkeleton rows={4} />}
      {error && (
        <section className="empty-state" aria-live="assertive">
          <h2 className="empty-state-title">Couldn&rsquo;t load your caseload</h2>
          <p className="empty-state-body">{error}</p>
          <button
            type="button"
            className="snap-button empty-state-cta"
            onClick={() => {
              setError(null);
              void load();
            }}
          >
            Try again
          </button>
        </section>
      )}

      {!loading && !error && students.length === 0 && (
        <EmptyState
          illustration={<StudentsIllustration />}
          title="No students yet"
          body="Add your first student and we&rsquo;ll walk you through setting up goals and scheduling sessions."
          cta={{
            label: 'Add student',
            onClick: onAddStudent,
          }}
        />
      )}

      {/* Filtered-empty state (#436 acceptance criterion #8). */}
      {!loading && !error && students.length > 0 && visible.length === 0 && (
        <section
          className="empty-state"
          aria-live="polite"
        >
          <h2 className="empty-state-title">
            No students match these filters.
          </h2>
          <p className="empty-state-body">
            Try a different search term, clear one of the chips, or
            reset everything below.
          </p>
          <button
            type="button"
            className="snap-button empty-state-cta"
            onClick={clearAllFilters}
          >
            Clear filters
          </button>
        </section>
      )}

      {/* Bulk-archive confirmation modal (#436 acceptance criterion #6). */}
      {confirmingArchive && (
        <div
          className="caseload-confirm-scrim"
          onClick={cancelArchive}
          aria-hidden="true"
        >
          <div
            className="caseload-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="caseload-archive-title"
            aria-describedby="caseload-archive-body"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="caseload-archive-title"
              className="caseload-confirm-title"
            >
              Archive {selectedIds.size}{' '}
              {selectedIds.size === 1 ? 'student' : 'students'}?
            </h2>
            <p
              id="caseload-archive-body"
              className="caseload-confirm-body"
            >
              Archived students are hidden from the caseload. You can
              restore them later from Settings.
            </p>
            {archiveError && (
              <p className="caseload-empty-filter tagline" role="alert">
                {archiveError}
              </p>
            )}
            <div className="caseload-confirm-actions">
              <button
                type="button"
                className="link-button"
                onClick={cancelArchive}
                disabled={archiving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="snap-button"
                onClick={() => {
                  void confirmArchive();
                }}
                disabled={archiving}
              >
                {archiving
                  ? 'Archiving…'
                  : `Archive ${selectedIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDesktop ? (
        <section
          className="caseload-grid"
          aria-label="Caseload"
          /* min-height prevents Cumulative Layout Shift when the
           * grid reflows after a filter change. */
          style={{ minHeight: 320 }}
        >
          {visible.length === 0
            ? null
            : groupedSections
            ? groupedSections.map((section) => (
                <section
                  key={section.bucket}
                  className="caseload-section"
                  aria-label={section.label}
                >
                  <h3 className="caseload-section-header">
                    {section.label}
                    <span className="caseload-section-count">
                      {section.students.length}
                    </span>
                  </h3>
                  {section.students.map((s) => (
                    <StudentCardOrRow
                      key={s.id}
                      student={s}
                      isDesktop
                      selected={selectedIds.has(s.id)}
                      selectMode={selectMode}
                      onClick={() => handleCardClick(s.id)}
                      onToggle={() => toggleSelected(s.id)}
                    />
                  ))}
                </section>
              ))
            : visible.map((s) => (
                <StudentCardOrRow
                  key={s.id}
                  student={s}
                  isDesktop
                  selected={selectedIds.has(s.id)}
                  selectMode={selectMode}
                  onClick={() => handleCardClick(s.id)}
                  onToggle={() => toggleSelected(s.id)}
                />
              ))}
        </section>
      ) : (
        <section className="caseload-list" aria-label="Caseload">
          {visible.length === 0
            ? null
            : groupedSections
            ? groupedSections.map((section) => (
                <section
                  key={section.bucket}
                  className="caseload-section"
                  aria-label={section.label}
                >
                  <h3 className="caseload-section-header">
                    {section.label}
                    <span className="caseload-section-count">
                      {section.students.length}
                    </span>
                  </h3>
                  <div className="caseload-section-rows">
                    {section.students.map((s) => (
                      <StudentCardOrRow
                        key={s.id}
                        student={s}
                        isDesktop={false}
                        selected={selectedIds.has(s.id)}
                        selectMode={selectMode}
                        onClick={() => handleCardClick(s.id)}
                        onToggle={() => toggleSelected(s.id)}
                      />
                    ))}
                  </div>
                </section>
              ))
            : visible.map((s) => (
                <StudentCardOrRow
                  key={s.id}
                  student={s}
                  isDesktop={false}
                  selected={selectedIds.has(s.id)}
                  selectMode={selectMode}
                  onClick={() => handleCardClick(s.id)}
                  onToggle={() => toggleSelected(s.id)}
                />
              ))}
        </section>
      )}
    </main>
  );
}

interface StudentCardOrRowProps {
  student: StudentSummary;
  isDesktop: boolean;
  selected: boolean;
  selectMode: boolean;
  onClick: () => void;
  onToggle: () => void;
}

function StudentCardOrRow({
  student,
  isDesktop,
  selected,
  selectMode,
  onClick,
  onToggle,
}: StudentCardOrRowProps): ReactElement {
  const categoryChip =
    student.disorderCategory === 'aac'
      ? 'AAC'
      : labelForDisorderCategory(student.disorderCategory);
  const showCheckbox = selectMode || selected;
  const checkboxLabel = selected
    ? `Deselect ${student.firstName}`
    : `Select ${student.firstName}`;

  // The select-mode checkbox must NOT be rendered (not just visually
  // hidden) when selectMode is off and the row is unselected:
  // (1) an opacity:0 + pointer-events:none input is still in the
  //     keyboard tab order, so screen-reader and keyboard users
  //     tabbed through the caseload hit invisible, unlabeled-in-
  //     context controls on every row (#486).
  // (2) nesting a <label>/<input> inside the row <button> is
  //     invalid HTML and produces unpredictable accessible-name
  //     and activation behaviour across screen readers.
  // Render the checkbox as a SIBLING of the row button (the desktop
  // .caseload-card-wrapper already does this) and only mount it
  // when the row is in select mode or already selected.
  if (isDesktop) {
    return (
      <div className="caseload-card-wrapper">
        <button
          type="button"
          className={
            selected
              ? 'caseload-card caseload-card-selected'
              : 'caseload-card'
          }
          onClick={onClick}
        >
          <div className="caseload-card-head">
            <StudentAvatar
              firstName={student.firstName}
              seed={student.id}
              size={64}
            />
            <div className="caseload-card-name">
              <span className="caseload-row-name">{student.firstName}</span>
              <span className="caseload-row-meta">
                {student.ageYears != null ? `Age ${student.ageYears}` : ''}
              </span>
            </div>
            <span className="caseload-chip">{categoryChip}</span>
          </div>
          <div className="caseload-card-body">
            <span className="caseload-row-meta">
              {student.activeGoalsCount} active goal
              {student.activeGoalsCount === 1 ? '' : 's'} · Last session{' '}
              {student.lastSessionDate
                ? formatShortDate(student.lastSessionDate)
                : 'never'}
            </span>
          </div>
        </button>
        {showCheckbox && (
          <label className="caseload-card-checkbox">
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggle}
              aria-label={checkboxLabel}
            />
          </label>
        )}
      </div>
    );
  }

  return (
    <div className="caseload-row-wrapper">
      <button
        type="button"
        className={
          selected ? 'caseload-row caseload-row-selected' : 'caseload-row'
        }
        onClick={onClick}
      >
        <StudentAvatar
          firstName={student.firstName}
          seed={student.id}
          size={44}
        />
        <div className="caseload-row-body">
          <div className="caseload-row-main">
            <span className="caseload-row-name">{student.firstName}</span>
            <span className="caseload-chip">{categoryChip}</span>
          </div>
          <span className="caseload-row-meta">
            {student.ageYears != null ? `Age ${student.ageYears} · ` : ''}
            {student.activeGoalsCount} active goal
            {student.activeGoalsCount === 1 ? '' : 's'} · Last session{' '}
            {student.lastSessionDate
              ? formatShortDate(student.lastSessionDate)
              : 'never'}
          </span>
        </div>
        {!showCheckbox && (
          <span className="caseload-chevron" aria-hidden="true">
            ›
          </span>
        )}
      </button>
      {showCheckbox && (
        <label className="caseload-row-checkbox">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            aria-label={checkboxLabel}
          />
        </label>
      )}
    </div>
  );
}
